// app/frontend/pages/Areas/AreaShowView.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Notifications } from '@mantine/notifications';
import { Link } from '@inertiajs/react';
import { usePage, Head, router } from '@inertiajs/react';
import { PageProps } from '@inertiajs/core';
import Helmet from 'react-helmet';
import { useUser } from '../../src/context/UserContext';
import InteractionButtons from '../../components/Posts/InteractionButtons';
import {
  Container,
  Text,
  Button,
  Tabs,
  Group,
  Title,
  Badge,
  Box,
  Grid,
  Drawer,
  Stack,
  Divider,
  TextInput,
  MultiSelect,
  ActionIcon,
  MantineProvider,
  Card,
  Image,
  Loader,
  SimpleGrid,
  Paper,
} from '@mantine/core';
import '../../styles/ScrollTabs.css';
import { useScrollableTabs } from '../../src/hooks/useScrollableTabs';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconSearch,
  IconAdjustmentsHorizontal,
  IconCalendarDown,
  IconCalendarUp,
  IconUsersGroup,
  IconCalendarEvent,
} from '@tabler/icons-react';
import EventCard from '../../components/Cards/EventCard';
import ArtistCard from '../../components/Cards/ArtistCard';
import OrganizerCard from '../../components/Cards/OrganizerCard';
import DynamicDrawer from '../../components/Drawer/DynamicDrawer';

import EventDrawerContent from '../../components/Drawer/EventDrawerContent';
import ArtistDrawerContent from '../../components/Drawer/ArtistDrawerContent';
import OrganizerDrawerContent from '../../components/Drawer/OrganizerDrawerContent';
import { StablePagination } from '../../components/StablePagination';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import baseImage from '../../assets/images/BASE.jpg';
import '../../styles/AreaShow.css';

// Interfaces
interface Event {
  id: number;
  name: string;
  event_start_date: string;
  event_end_date: string;
  image_url?: string;
  event_types?: { id: number; name: string }[];
  dance_styles?: { id: number; name: string }[];
  dance_style_ids?: number[];
  slug?: string;
  [key: string]: any;
}

interface Artist {
  id: number;
  name: string;
  slug: string;
  description?: string;
  avatar_url?: string;
  [key: string]: any;
}

interface Organizer {
  id: number;
  name: string;
  slug: string;
  description?: string;
  website_url?: string;
  facebook_url?: string;
  instagram_url?: string;
}

interface Area {
  id: number;
  name: string;
  events_count: number;
  artists?: Artist[];
  skyline_image_url?: string;
  meta_image_url?: string;
  slug?: string;
  can_edit?: boolean;
  power_users: string[];
}

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  url: string;
  backgroundColor: string;
  borderColor: string;
}

interface CalendarMetadata {
  month: number;
  year: number;
}

// Cache interface for month-based calendar events
interface CalendarCache {
  [key: string]: CalendarEvent[];
}

interface AreaShowProps extends PageProps {
  area: Area;
  upcoming_events?: Event[];
  past_events?: Event[];
  calendar_events?: Event[];
  calendar_metadata?: CalendarMetadata;
  artists?: Artist[];
  organizers?: Organizer[];
  upcoming_total_count?: number;
  past_total_count?: number;
  organizers_total_count?: number;
  page_meta?: {
    title: string;
    description: string;
    url: string;
    image: string;
  };
  upcoming_pagination?: {
    current_page: number;
    per_page: number;
    total_pages: number;
  };
  past_pagination?: {
    current_page: number;
    per_page: number;
    total_pages: number;
  };
  artists_pagination?: {
    current_page: number;
    per_page: number;
    total_pages: number;
  };
  organizers_pagination?: {
    current_page: number;
    per_page: number;
    total_pages: number;
  };
  artists_total_count?: number;
  eventTypes: { id: number; name: string }[];
  danceStyles: { id: number; name: string }[];
  filters: {
    name?: string;
    dance_style_ids?: number[];
    event_type_ids?: number[];
  };
  active_tab: string;
  can_edit: boolean;
  power_users: string[];
}

export default function AreaShowView() {
  const props = usePage<AreaShowProps>().props;
  const { area, active_tab, filters, eventTypes, danceStyles, page_meta, power_users } = props;
  const { user } = useUser();
  const isSuperAdmin = user?.roles.includes('SuperAdmin');
  const can_edit = area.can_edit;

  const { tabListReference } = useScrollableTabs();

  // Access tab-specific data safely
  const upcoming_events = props.upcoming_events || [];
  const past_events = props.past_events || [];
  const calendar_events = props.calendar_events || [];
  const calendar_metadata = props.calendar_metadata || {
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  };
  const artists = props.artists || [];
  const upcoming_pagination = props.upcoming_pagination || { current_page: 1, per_page: 20, total_pages: 1 };
  const past_pagination = props.past_pagination || { current_page: 1, per_page: 20, total_pages: 1 };
  const upcoming_total_count = props.upcoming_total_count || 0;
  const past_total_count = props.past_total_count || 0;
  const artists_total_count = props.artists_total_count || 0;
  const organizers_total_count = props.organizers_total_count || 0;
  const organizers = props.organizers || [];

  // == ALL STATE DECLARATIONS FIRST ==
  // General states
  const [activeTab, setActiveTab] = useState(active_tab || 'upcoming');
  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);

  // Calendar-specific states
  const [eventsCache, setEventsCache] = useState<CalendarCache>({});
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState<number>(calendar_metadata.month);
  const [currentCalendarYear, setCurrentCalendarYear] = useState<number>(calendar_metadata.year);
  const calendarRef = useRef<any>(null);

  // Debounce state
  const [debouncingSearch, setDebouncingSearch] = useState(false);

  // Pagination states
  const [upcomingCurrentPage, setUpcomingCurrentPage] = useState(upcoming_pagination?.current_page || 1);
  const [pastCurrentPage, setPastCurrentPage] = useState(past_pagination?.current_page || 1);
  const [artistsCurrentPage, setArtistsCurrentPage] = useState(props.artists_pagination?.current_page || 1);
  const [organizersCurrentPage, setOrganizersCurrentPage] = useState(props.organizers_pagination?.current_page || 1);

  // Filter states
  const [searchName, setSearchName] = useState(filters?.name || '');
  const [searchNameInput, setSearchNameInput] = useState(filters?.name || '');
  const [selectedDanceStyles, setSelectedDanceStyles] = useState(filters?.dance_style_ids || []);
  const [selectedEventTypes, setSelectedEventTypes] = useState(filters?.event_type_ids || []);

  // Drawer states
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerContentType, setDrawerContentType] = useState<'event' | 'artist' | 'organizer'>('event');

  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [selectedOrganizer, setSelectedOrganizer] = useState<Organizer | null>(null);

  // == CONSTANTS ==
  const ITEMS_PER_PAGE = 20;
  const DEBOUNCE_DELAY = 500; // 500ms debounce delay
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const baseOnlyCounts = ['upcoming_total_count', 'past_total_count', 'artists_total_count', 'organizers_total_count'];

  const skylineImageUrl = area.skyline_image_url;
  console.log('skylineImageUrl:', skylineImageUrl);

  const areaMetaImageUrl = area.meta_image_url || skylineImageUrl;
  console.log('areaMetaImageUrl:', areaMetaImageUrl);
  // == COMPUTED VALUES (based on state) ==
  // Options for filters
  const eventTypeOptions = eventTypes.map((et) => ({
    value: et.id.toString(),
    label: et.name,
  }));

  const danceStyleOptions = danceStyles.map((ds) => ({
    value: ds.id.toString(),
    label: ds.name,
  }));

  // Calculate active filter count
  const activeFilterCount = (searchName ? 1 : 0) + selectedDanceStyles.length + selectedEventTypes.length;

  // == UTILITY FUNCTIONS ==
  // Build filter parameters for API calls
  const buildFilterParams = () => {
    const filterParams: Record<string, any> = {};

    if (searchName) filterParams.name = searchName;
    if (selectedDanceStyles.length) filterParams.dance_style_ids = selectedDanceStyles;
    if (selectedEventTypes.length) filterParams.event_type_ids = selectedEventTypes;

    return Object.keys(filterParams).length > 0 ? filterParams : undefined;
  };

  // Debounced apply filters function
  const debouncedApplyFilters = useCallback(
    debounce(() => {
      setDebouncingSearch(false);
      applyFilters();
    }, DEBOUNCE_DELAY),
    [searchName, selectedDanceStyles, selectedEventTypes, activeTab],
  );

  // Create a debounce function
  function debounce(func: Function, delay: number) {
    let timeoutId: NodeJS.Timeout;
    return function (...args: any[]) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  }

  // Create a cache key based on year and month
  const createCacheKey = (year: number, month: number) => {
    const filterParams = buildFilterParams();
    const filterString = filterParams ? JSON.stringify(filterParams) : '';
    return `${year}-${month}-${filterString}`;
  };

  // Format events for calendar display
  const formatEventsForCalendar = (events: Event[]): CalendarEvent[] => {
    return events.map((event) => ({
      id: event.id?.toString() || `event-${Math.random().toString(36).substr(2, 9)}`,
      title: event.name || 'Untitled Event',
      start: event.event_start_date,
      end: event.event_end_date,
      url: event.slug ? `/events/${event.slug}` : '#',
      backgroundColor: '#ea580c',
      borderColor: '#ea580c',
    }));
  };

  // == EVENT HANDLERS ==
  // Handle dates change in calendar (when user navigates to a different month)
  const handleDatesSet = (dateInfo: any) => {
    const calendarDate = dateInfo.view.currentStart;
    const month = calendarDate.getMonth() + 1; // FullCalendar months are 0-indexed
    const year = calendarDate.getFullYear();

    // Only fetch if month/year has changed
    if (month !== currentCalendarMonth || year !== currentCalendarYear) {
      setCurrentCalendarMonth(month);
      setCurrentCalendarYear(year);

      const cacheKey = createCacheKey(year, month);

      // Check if we already have events for this month in cache
      if (eventsCache[cacheKey]) {
        setCalendarEvents(eventsCache[cacheKey]);
      } else {
        // Fetch events for this month from server
        fetchCalendarEvents(month, year);
      }
    }
  };

  // Fetch calendar events for a specific month and year
  const fetchCalendarEvents = (month: number, year: number) => {
    setIsLoading(true);

    const filterParams = buildFilterParams();
    const cacheKey = createCacheKey(year, month);

    router.visit(window.location.pathname, {
      data: {
        active_tab: 'calendar',
        month: month,
        year: year,
        filter: filterParams,
      },
      preserveState: true,
      preserveScroll: true,
      only: ['calendar_events', 'calendar_metadata', ...baseOnlyCounts],
      onFinish: () => {
        setIsLoading(false);
      },
    });
  };

  // Handle search name input change with debounce
  const handleSearchNameChange = (value: string) => {
    setSearchNameInput(value);
    setDebouncingSearch(true);
    setSearchName(value);
    debouncedApplyFilters();
  };

  // Handle dance style selection with debounce
  const handleDanceStyleChange = (value: string[]) => {
    setSelectedDanceStyles(value);
    setDebouncingSearch(true);
    debouncedApplyFilters();
  };

  // Handle event type selection with debounce
  const handleEventTypeChange = (value: string[]) => {
    setSelectedEventTypes(value);
    setDebouncingSearch(true);
    debouncedApplyFilters();
  };

  // Handle tab change - only fetch data for the selected tab
  const handleTabChange = (tab: string | null) => {
    if (!tab) return;

    setActiveTab(tab);
    setIsLoading(true);

    const filterParams = buildFilterParams();

    // 1) always include the four count props + filters/active_tab
    const onlyProps = [
      'active_tab',
      'filters',
      'upcoming_total_count',
      'past_total_count',
      'artists_total_count',
      'organizers_total_count',
    ];

    // 2) push tab‑specific data keys
    if (tab === 'upcoming') {
      onlyProps.push('upcoming_events', 'upcoming_pagination');
    } else if (tab === 'past') {
      onlyProps.push('past_events', 'past_pagination');
    } else if (tab === 'calendar') {
      onlyProps.push('calendar_events', 'calendar_metadata');
    } else if (tab === 'artists') {
      onlyProps.push('artists', 'artists_pagination');
    } else if (tab === 'organizers') {
      onlyProps.push('organizers', 'organizers_pagination');
    }

    // 3) build a single data object
    const data: Record<string, any> = {
      active_tab: tab,
      filter: filterParams,
    };

    // reset pagination on the event tabs
    if (tab === 'upcoming') data.upcoming_page = 1;
    if (tab === 'past') data.past_page = 1;

    // include month/year for calendar
    if (tab === 'calendar') {
      data.month = currentCalendarMonth;
      data.year = currentCalendarYear;
    }

    // optionally reset page for artists/organizers
    if (tab === 'artists') data.artists_page = 1;
    if (tab === 'organizers') data.organizers_page = 1;

    // 4) single visit
    router.visit(window.location.pathname, {
      data,
      preserveState: true,
      preserveScroll: true,
      only: onlyProps,
      onFinish: () => setIsLoading(false),
    });
  };

  const handleArtistsPageChange = (page: number) => {
    setIsLoading(true);
    setArtistsCurrentPage(page);

    const filterParams = buildFilterParams();

    router.visit(window.location.pathname, {
      data: {
        active_tab: 'artists',
        artists_page: page,
        filter: filterParams,
      },
      preserveState: true,
      only: ['artists', 'artists_pagination', 'artists_total_count', ...baseOnlyCounts],
      onFinish: () => {
        setIsLoading(false);
      },
    });
  };

  const handleOrganizerClick = (organizer: Organizer) => {
    setSelectedOrganizer(organizer);
    setDrawerContentType('organizer');
    setDrawerOpened(true);
  };

  const handleOrganizersPageChange = (page: number) => {
    setIsLoading(true);
    setOrganizersCurrentPage(page);

    const filterParams = buildFilterParams();

    router.visit(window.location.pathname, {
      data: {
        active_tab: 'organizers',
        organizers_page: page,
        filter: filterParams,
      },
      preserveState: true,
      only: ['organizers', 'organizers_pagination', 'organizers_total_count', ...baseOnlyCounts],
      onFinish: () => {
        setIsLoading(false);
      },
    });
  };

  // Handle upcoming events pagination
  const handleUpcomingPageChange = (page: number) => {
    setIsLoading(true);
    setUpcomingCurrentPage(page);

    const filterParams = buildFilterParams();

    router.visit(window.location.pathname, {
      data: {
        active_tab: 'upcoming',
        upcoming_page: page,
        filter: filterParams,
      },
      preserveState: true,
      only: ['upcoming_events', 'upcoming_pagination', 'upcoming_total_count', ...baseOnlyCounts],
      onFinish: () => {
        setIsLoading(false);
      },
    });
  };

  // Handle past events pagination
  const handlePastPageChange = (page: number) => {
    setIsLoading(true);
    setPastCurrentPage(page);

    const filterParams = buildFilterParams();

    router.visit(window.location.pathname, {
      data: {
        active_tab: 'past',
        past_page: page,
        filter: filterParams,
      },
      preserveState: true,
      only: ['past_events', 'past_pagination', 'past_total_count', ...baseOnlyCounts],
      onFinish: () => {
        setIsLoading(false);
      },
    });
  };

  // Apply filters - called directly from drawer and also from debounced functions
  const applyFilters = () => {
    setIsLoading(true);

    // reset pages
    setUpcomingCurrentPage(1);
    setPastCurrentPage(1);
    // (optional) reset artists/organizers page too

    const filterParams = buildFilterParams();

    // 1) always include these counts
    const onlyProps = ['active_tab', 'filters', ...baseOnlyCounts];

    // 2) plus the active‑tab data
    if (activeTab === 'upcoming') {
      onlyProps.push('upcoming_events', 'upcoming_pagination');
    } else if (activeTab === 'past') {
      onlyProps.push('past_events', 'past_pagination');
    } else if (activeTab === 'calendar') {
      onlyProps.push('calendar_events', 'calendar_metadata');
    } else if (activeTab === 'artists') {
      onlyProps.push('artists', 'artists_pagination');
    } else if (activeTab === 'organizers') {
      onlyProps.push('organizers', 'organizers_pagination');
    }

    // 3) build one data payload
    const data: Record<string, any> = {
      active_tab: activeTab,
      filter: filterParams,
    };

    // reset pagination params
    if (activeTab === 'upcoming') data.upcoming_page = 1;
    if (activeTab === 'past') data.past_page = 1;
    if (activeTab === 'calendar') {
      data.month = currentCalendarMonth;
      data.year = currentCalendarYear;
    }
    if (activeTab === 'artists') data.artists_page = 1;
    if (activeTab === 'organizers') data.organizers_page = 1;

    // 4) single Inertia visit
    router.visit(window.location.pathname, {
      data,
      preserveState: true,
      preserveScroll: true,
      only: onlyProps,
      onFinish: () => {
        setIsLoading(false);
        if (activeTab === 'calendar') setEventsCache({}); // clear cache on calendar
      },
    });
  };

  // Clear all filters with debounce
  const clearFilters = () => {
    setSearchName('');
    setSearchNameInput('');
    setSelectedDanceStyles([]);
    setSelectedEventTypes([]);
    setDebouncingSearch(true);
    debouncedApplyFilters();

    // Clear the events cache when filters change
    setEventsCache({});

    setTabCounts({
      upcoming: initialUpcoming,
      past: initialPast,
      artists: initialArtists || 0,
      organizers: initialOrganizers || 0,
    });
  };

  // Handle event click
  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setDrawerContentType('event');
    setDrawerOpened(true);
  };

  // Handle artist click
  const handleArtistClick = (artist: Artist) => {
    setSelectedArtist(artist);
    setDrawerContentType('artist');
    setDrawerOpened(true);
  };

  const {
    upcoming_total_count: initialUpcoming,
    past_total_count: initialPast,
    artists_total_count: initialArtists,
    organizers_total_count: initialOrganizers,
  } = props;

  const [tabCounts, setTabCounts] = useState({
    upcoming: initialUpcoming,
    past: initialPast,
    artists: initialArtists,
    organizers: initialOrganizers,
  });

  const isUserSignedIn = !!user;

  // == EFFECTS ==
  // Set up calendar events when calendar data changes
  // Effect 1: Update the calendar view when parameters change
  useEffect(() => {
    // If you need to manually update the document title and meta tags
    if (page_meta?.title) {
      document.title = page_meta.title;
    }

    // console.log('meta:', page_meta);
    console.log('Can edit:', area.can_edit);

    // Optional: Update meta description directly if Helmet doesn't work
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page_meta?.description) {
      metaDescription.setAttribute('content', page_meta.description);
    }
  }, [page_meta]);

  useEffect(() => {
    if (activeTab === 'calendar' && calendarRef.current && !isLoading) {
      const calendarApi = calendarRef.current.getApi();
      const apiDate = calendarApi.getDate();
      console.log('Effect1:', {
        activeTab,
        isLoading,
        currentCalendarMonth,
        currentCalendarYear,
        apiMonth: apiDate.getMonth() + 1,
        apiYear: apiDate.getFullYear(),
      });
      if (currentCalendarMonth !== apiDate.getMonth() + 1 || currentCalendarYear !== apiDate.getFullYear()) {
        calendarApi.gotoDate(new Date(currentCalendarYear, currentCalendarMonth - 1, 1));
      }
    }
  }, [activeTab, isLoading, currentCalendarMonth, currentCalendarYear]);

  // Effect 2 — run *only* on a new Inertia payload
  useEffect(() => {
    if (activeTab !== 'calendar') return;
    if (!props.calendar_events?.length) return;
    if (!props.calendar_metadata) return;

    const { month, year } = props.calendar_metadata;
    const formatted = formatEventsForCalendar(props.calendar_events);
    setCalendarEvents(formatted);

    // **Use the same helper** so your cache key matches what you check in Effect 3
    const cacheKey = createCacheKey(year, month);
    setEventsCache((prev) => ({
      ...prev,
      [cacheKey]: formatted,
    }));
  }, [
    activeTab,
    props.calendar_events,
    props.calendar_metadata?.month ?? new Date().getMonth() + 1,
    props.calendar_metadata?.year ?? new Date().getFullYear(),
  ]);

  // Effect 3: Initialize calendar events for current month when tab changes or when month/year changes
  useEffect(() => {
    if (activeTab !== 'calendar') return;
    const month = currentCalendarMonth;
    const year = currentCalendarYear;
    const cacheKey = createCacheKey(year, month);

    if (eventsCache[cacheKey]) {
      setCalendarEvents(eventsCache[cacheKey]);
    } else {
      fetchCalendarEvents(month, year);
    }
  }, [activeTab, currentCalendarMonth, currentCalendarYear, eventsCache]);

  // Effect 4: Clear calendar events when leaving calendar tab
  useEffect(() => {
    if (activeTab !== 'calendar') {
      setCalendarEvents([]);
    }
  }, [activeTab]);

  useEffect(() => {
    setTabCounts({
      upcoming: props.upcoming_total_count,
      past: props.past_total_count,
      artists: props.artists_total_count || 0,
      organizers: props.organizers_total_count || 0,
    });
  }, [props.upcoming_total_count, props.past_total_count, props.artists_total_count, props.organizers_total_count]);

  // == STYLES ==
  const headerStyle = {
    position: 'relative',
    height: '250px',
    marginBottom: '2rem',
    marginLeft: 'calc(-50vw + 50%)',
    marginRight: 'calc(-50vw + 50%)',
    width: '100vw',
    backgroundImage: area.skyline_image_url ? `url(${area.skyline_image_url})` : `url(${baseImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    overflow: 'hidden',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  };

  const baseOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.4))',
    zIndex: 1,
  };

  const gradientOverlayStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: 'linear-gradient(to bottom, rgba(24, 24, 27, 0.5) 0%, rgba(24, 24, 27, 0.95) 100%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    padding: '2rem',
    zIndex: 2,
  };

  // == RENDER ==
  return (
    <MantineProvider>
      {page_meta && (
        <Helmet>
          <title>{page_meta.title}</title>
          <meta name="description" content={page_meta.description} />

          {/* Open Graph tags */}
          <meta property="og:title" content={page_meta.title} />
          <meta property="og:description" content={page_meta.description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page_meta.url} />
          <meta property="og:image" content={page_meta.image} />

          {/* Twitter Card tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={page_meta.title} />
          <meta name="twitter:description" content={page_meta.description} />
          <meta name="twitter:image" content={page_meta.image} />
        </Helmet>
      )}
      <Container size="md" mb="8rem" style={{ minHeight: '100vh' }} px={isMobile ? 0 : undefined}>
        <Notifications position="top-right" zIndex={2077} />
        {/* Area Header */}
        <Box style={headerStyle} mb="20px">
          <div style={baseOverlayStyle}>
            <div style={gradientOverlayStyle}>
              <div className="content-wrapper">
                <Title order={1} mb="md" className="rubik-header" style={{ color: '#FFF7ED', fontSize: '2.5rem' }}>
                  {area.name}
                </Title>

                {/* Search and Filter Bar */}
                <Group justify="apart" mb="md">
                  <TextInput
                    placeholder={searchNameInput ? undefined : 'Search events...'}
                    value={searchNameInput}
                    onChange={(e) => handleSearchNameChange(e.currentTarget.value)}
                    style={{ flex: 1 }}
                    leftSection={<IconSearch size={16} />}
                    rightSection={debouncingSearch && <Loader size="xs" color="orange" />}
                  />

                  <Box pos="relative">
                    <ActionIcon variant="light" size="xl" color="orange" onClick={() => setFilterDrawerOpened(true)}>
                      <IconAdjustmentsHorizontal />
                    </ActionIcon>
                    {activeFilterCount > 0 && (
                      <Badge
                        color="orange"
                        size="sm"
                        radius="xl"
                        style={{ position: 'absolute', top: -10, right: -10 }}
                      >
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Box>
                </Group>
              </div>
            </div>
          </div>
        </Box>

        <Box w="95%" my="8px" mx="8px">
          <InteractionButtons
            context={['area']}
            isUserSignedIn={isUserSignedIn}
            object={area}
            metaImageUrl={areaMetaImageUrl}
          />
        </Box>

        {/* Tabs for Upcoming and Past Events */}
        <Tabs value={activeTab} onChange={handleTabChange} mt="20px" color="orange">
          <Tabs.List ref={tabListReference}>
            <Tabs.Tab
              value="upcoming"
              leftSection={<IconCalendarUp size={20} stroke={1.5} color="#FFF7ED" />}
              style={{ color: '#FFF7ED', fontWeight: 'bold' }}
              rightSection={
                <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
                  {tabCounts.upcoming}
                </Badge>
              }
            >
              Upcoming Events
            </Tabs.Tab>

            <Tabs.Tab
              value="past"
              leftSection={<IconCalendarDown size={20} stroke={1.5} color="#FFF7ED" />}
              style={{ color: '#FFF7ED', fontWeight: 'bold' }}
              rightSection={
                <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
                  {tabCounts.past}
                </Badge>
              }
            >
              Past Events
            </Tabs.Tab>

            <Tabs.Tab
              value="calendar"
              leftSection={<IconCalendarEvent size={20} stroke={1.5} color="#FFF7ED" />}
              style={{ color: '#FFF7ED', fontWeight: 'bold' }}
            >
              Calendar View
            </Tabs.Tab>

            <Tabs.Tab
              value="artists"
              leftSection={<IconUsersGroup size={20} stroke={1.5} color="#FFF7ED" />}
              style={{ color: '#FFF7ED', fontWeight: 'bold' }}
              rightSection={
                <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
                  {tabCounts.artists}
                </Badge>
              }
            >
              Artists
            </Tabs.Tab>

            <Tabs.Tab
              value="organizers"
              leftSection={<IconUsersGroup size={20} stroke={1.5} color="#FFF7ED" />}
              style={{ color: '#FFF7ED', fontWeight: 'bold' }}
              rightSection={
                <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
                  {tabCounts.organizers}
                </Badge>
              }
            >
              Organizers
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="upcoming" pt="md" px="xs">
            <Group justify="flex-start" mb="md">
              <Text className="text-white poppins">
                Showing {upcoming_events.length} of {upcoming_total_count} upcoming events
                {(isLoading || debouncingSearch) && <Loader color="orange" size="xs" ml="md" />}
              </Text>
            </Group>

            {upcoming_pagination && upcoming_pagination.total_pages > 1 && (
              <Group justify="flex-start" mb="md">
                <StablePagination
                  total={upcoming_pagination.total_pages}
                  value={upcomingCurrentPage}
                  onChange={handleUpcomingPageChange}
                  color="#EA580C"
                />
              </Group>
            )}
            {upcoming_events.length > 0 ? (
              <>
                <Grid mb="lg">
                  {upcoming_events.map((event) => (
                    <Grid.Col span={{ base: 12, sm: 4, lg: 4 }} key={event.id}>
                      <EventCard event={event} onCardClick={() => handleEventClick(event)} />
                    </Grid.Col>
                  ))}
                </Grid>

                {upcoming_pagination && upcoming_pagination.total_pages > 1 && (
                  <Group justify="flex-start" mb="md">
                    <StablePagination
                      total={upcoming_pagination.total_pages}
                      value={upcomingCurrentPage}
                      onChange={handleUpcomingPageChange}
                      color="#EA580C"
                    />
                  </Group>
                )}
              </>
            ) : (
              <Paper p="lg" className="fade-in" radius="md" mt="xl" style={{ backgroundColor: '#8b8b8b50' }}>
                <Stack gap="sm" align="flex-start">
                  <Text size="lg" fw={600} c="white">
                    {activeTab === 'upcoming' &&
                      'No upcoming events found with these filters. Do you know of an event that should be here? Feel free to list an event!'}
                    {activeTab === 'past' && 'No past events found. Help the community by adding event history!'}
                    {activeTab === 'artists' &&
                      'No artists found. Do you know an artist that should be listed here? Feel free to list an artist to help the local and global community!'}
                    {activeTab === 'organizers' &&
                      'No organizers found. Do you know of an organizer that should be listed here? Let them know it’s free to be listed on our platform!'}
                  </Text>

                  <Text size="sm" c="white">
                    {activeTab === 'artists'
                      ? "You don't need to be the artist or their manager to add an artist and support the local and global community."
                      : activeTab === 'organizers'
                        ? "Encourage organizers you know to join — it's free to be listed and helps grow the local and global community!"
                        : "You don't need to be the event organizer to add events to support the local and global dance community."}
                  </Text>

                  <Button
                    component="a"
                    href={
                      activeTab === 'artists'
                        ? '/suggest_artist'
                        : activeTab === 'organizers'
                          ? '/learn-more'
                          : '/suggest_event'
                    }
                    color="#EA580C"
                    radius="sm"
                    size="md"
                  >
                    {activeTab === 'artists' ? 'Add Artist' : activeTab === 'organizers' ? 'Learn More' : 'Add Event'}
                  </Button>
                </Stack>
              </Paper>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="past" pt="md" px="xs">
            <Group justify="flex-start" mb="md">
              <Text className="text-white poppins">
                Showing {past_events.length} of {past_total_count} past events
                {(isLoading || debouncingSearch) && <Loader color="orange" size="xs" ml="md" />}
              </Text>
            </Group>

            {past_pagination && past_pagination.total_pages > 1 && (
              <Group justify="flex-start" mb="md">
                <StablePagination
                  total={past_pagination.total_pages}
                  value={pastCurrentPage}
                  onChange={handlePastPageChange}
                  color="#EA580C"
                />
              </Group>
            )}
            {past_events.length > 0 ? (
              <>
                <Grid mb="lg">
                  {past_events.map((event) => (
                    <Grid.Col span={{ base: 12, sm: 4, lg: 4 }} key={event.id}>
                      <EventCard event={event} onCardClick={() => handleEventClick(event)} />
                    </Grid.Col>
                  ))}
                </Grid>

                {past_pagination && past_pagination.total_pages > 1 && (
                  <Group justify="flex-start" mb="md">
                    <StablePagination
                      total={past_pagination.total_pages}
                      value={pastCurrentPage}
                      onChange={handlePastPageChange}
                      color="#EA580C"
                    />
                  </Group>
                )}
              </>
            ) : (
              <Paper p="lg" className="fade-in" radius="md" mt="xl" style={{ backgroundColor: '#8b8b8b50' }}>
                <Stack gap="sm" align="flex-start">
                  <Text size="lg" fw={600} c="white">
                    {activeTab === 'upcoming' &&
                      'No upcoming events found with these filters. Do you know of an event that should be here? Feel free to list an event!'}
                    {activeTab === 'past' && 'No past events found. Help the community by adding event history!'}
                    {activeTab === 'artists' &&
                      'No artists found. Do you know an artist that should be listed here? Feel free to list an artist to help the local and global community!'}
                    {activeTab === 'organizers' &&
                      'No organizers found. Do you know of an organizer that should be listed here? Let them know it’s free to be listed on our platform!'}
                  </Text>

                  <Text size="sm" c="white">
                    {activeTab === 'artists'
                      ? "You don't need to be the artist or their manager to add an artist and support the local and global community."
                      : activeTab === 'organizers'
                        ? "Encourage organizers you know to join — it's free to be listed and helps grow the local and global community!"
                        : "You don't need to be the event organizer to add events to support the local and global dance community."}
                  </Text>

                  <Button
                    component="a"
                    href={
                      activeTab === 'artists'
                        ? '/suggest_artist'
                        : activeTab === 'organizers'
                          ? '/learn-more'
                          : '/suggest_event'
                    }
                    color="#EA580C"
                    radius="sm"
                    size="md"
                  >
                    {activeTab === 'artists' ? 'Add Artist' : activeTab === 'organizers' ? 'Learn More' : 'Add Event'}
                  </Button>
                </Stack>
              </Paper>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="calendar" pt="md" px="xs">
            <Box mt="md" style={{ height: 'auto' }}>
              {isLoading || debouncingSearch ? (
                <Group justify="center" style={{ padding: '2rem' }}>
                  <Loader color="orange" size="lg" />
                </Group>
              ) : (
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  themeSystem="standard"
                  height="auto"
                  datesSet={handleDatesSet}
                  events={calendarEvents}
                  displayEventTime={false}
                  eventClick={(info) => {
                    // Prevent default navigation
                    info.jsEvent.preventDefault();

                    // Extract slug from the URL
                    let eventSlug = null;
                    if (info.event.url) {
                      // Get the last part of the URL which should be the slug
                      const urlParts = info.event.url.split('/');
                      eventSlug = urlParts[urlParts.length - 1];
                    }

                    if (!eventSlug) {
                      console.error('Could not extract slug from URL:', info.event.url);
                      return;
                    }

                    // First check in paginated events (these have complete data)
                    const paginatedEvents = [...upcoming_events, ...past_events];
                    let fullEvent = paginatedEvents.find((e) => e.slug === eventSlug);

                    // If not found in paginated events, check in calendar_events
                    if (!fullEvent) {
                      fullEvent = calendar_events.find((e) => e.slug === eventSlug);
                    }

                    if (fullEvent) {
                      // Call the handler with the complete event object
                      handleEventClick(fullEvent);
                    } else {
                      console.error('No matching event found for slug:', eventSlug);
                    }
                  }}
                />
              )}
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="artists" pt="md" px="xs">
            <Group justify="flex-start" mb="md">
              <Text className="text-white poppins">
                Showing {artists.length} of {props.artists_total_count || 0} artists
                {(isLoading || debouncingSearch) && <Loader color="orange" size="xs" ml="md" />}
              </Text>
            </Group>

            {props.artists_pagination && props.artists_pagination.total_pages > 1 && (
              <Group justify="flex-start" mb="md">
                <StablePagination
                  total={props.artists_pagination.total_pages}
                  value={artistsCurrentPage}
                  onChange={handleArtistsPageChange}
                  color="#EA580C"
                />
              </Group>
            )}

            {isLoading || debouncingSearch ? (
              <Group justify="center" style={{ padding: '2rem' }}>
                <Loader color="orange" size="lg" />
              </Group>
            ) : artists && artists.length > 0 ? (
              <>
                <Grid>
                  {artists.map((artist) => (
                    <Grid.Col span={{ base: 6, sm: 3, md: 3 }} key={artist.id}>
                      <ArtistCard artist={artist} onCardClick={() => handleArtistClick(artist)} />
                    </Grid.Col>
                  ))}
                </Grid>

                {props.artists_pagination && props.artists_pagination.total_pages > 1 && (
                  <Group justify="flex-start" mb="md" mt="md">
                    <StablePagination
                      total={props.artists_pagination.total_pages}
                      value={artistsCurrentPage}
                      onChange={handleArtistsPageChange}
                      color="#EA580C"
                    />
                  </Group>
                )}
              </>
            ) : (
              <Paper p="lg" className="fade-in" radius="md" mt="xl" style={{ backgroundColor: '#8b8b8b50' }}>
                <Stack gap="sm" align="flex-start">
                  <Text size="lg" fw={600} c="white">
                    {activeTab === 'upcoming' &&
                      'No upcoming events found with these filters. Do you know of an event that should be here? Feel free to list an event!'}
                    {activeTab === 'past' && 'No past events found. Help the community by adding event history!'}
                    {activeTab === 'artists' &&
                      'No artists found. Do you know an artist that should be listed here? Feel free to list an artist to help the local and global community!'}
                    {activeTab === 'organizers' &&
                      'No organizers found. Do you know of an organizer that should be listed here? Let them know it’s free to be listed on our platform!'}
                  </Text>

                  <Text size="sm" c="white">
                    {activeTab === 'artists'
                      ? "You don't need to be the artist or their manager to add an artist and support the local and global community."
                      : activeTab === 'organizers'
                        ? "Encourage organizers you know to join — it's free to be listed and helps grow the local and global community!"
                        : "You don't need to be the event organizer to add events to support the local and global dance community."}
                  </Text>

                  <Button
                    component="a"
                    href={
                      activeTab === 'artists'
                        ? '/suggest_artist'
                        : activeTab === 'organizers'
                          ? '/learn-more'
                          : '/suggest_event'
                    }
                    color="#EA580C"
                    radius="sm"
                    size="md"
                  >
                    {activeTab === 'artists' ? 'Add Artist' : activeTab === 'organizers' ? 'Learn More' : 'Add Event'}
                  </Button>
                </Stack>
              </Paper>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="organizers" pt="md" px="xs">
            <Group justify="flex-start" mb="md">
              <Text className="text-white poppins">
                Showing {props.organizers?.length || 0} of {props.organizers_total_count || 0} organizers
                {(isLoading || debouncingSearch) && <Loader color="orange" size="xs" ml="md" />}
              </Text>
            </Group>

            {props.organizers_pagination && props.organizers_pagination.total_pages > 1 && (
              <Group justify="flex-start" mb="md">
                <StablePagination
                  total={props.organizers_pagination.total_pages}
                  value={organizersCurrentPage}
                  onChange={handleOrganizersPageChange}
                  color="#EA580C"
                />
              </Group>
            )}

            {isLoading || debouncingSearch ? (
              <Group justify="center" style={{ padding: '2rem' }}>
                <Loader color="orange" size="lg" />
              </Group>
            ) : props.organizers && props.organizers.length > 0 ? (
              <>
                <SimpleGrid cols={{ base: 2, sm: 3, md: 3 }} spacing="lg">
                  {props.organizers.map((organizer) => (
                    <OrganizerCard key={organizer.id} organizer={organizer} onCardClick={handleOrganizerClick} />
                  ))}
                </SimpleGrid>

                {props.organizers_pagination && props.organizers_pagination.total_pages > 1 && (
                  <Group justify="flex-start" mb="md" mt="md">
                    <StablePagination
                      total={props.organizers_pagination.total_pages}
                      value={organizersCurrentPage}
                      onChange={handleOrganizersPageChange}
                      color="#EA580C"
                    />
                  </Group>
                )}
              </>
            ) : (
              <Paper p="lg" className="fade-in" radius="md" mt="xl" style={{ backgroundColor: '#8b8b8b50' }}>
                <Stack gap="sm" align="flex-start">
                  <Text size="lg" fw={600} c="white">
                    {activeTab === 'upcoming' &&
                      'No upcoming events found with these filters. Do you know of an event that should be here? Feel free to list an event!'}
                    {activeTab === 'past' && 'No past events found. Help the community by adding event history!'}
                    {activeTab === 'artists' &&
                      'No artists found. Do you know an artist that should be listed here? Feel free to list an artist to help the local and global community!'}
                    {activeTab === 'organizers' &&
                      'No organizers found. Do you know of an organizer that should be listed here? Let them know it’s free to be listed on our platform!'}
                  </Text>

                  <Text size="sm" c="white">
                    {activeTab === 'artists'
                      ? "You don't need to be the artist or their manager to add an artist and support the local and global community."
                      : activeTab === 'organizers'
                        ? "Encourage organizers you know to join — it's free to be listed and helps grow the local and global community!"
                        : "You don't need to be the event organizer to add events to support the local and global dance community."}
                  </Text>

                  <Button
                    component="a"
                    href={
                      activeTab === 'artists'
                        ? '/suggest_artist'
                        : activeTab === 'organizers'
                          ? '/learn-more'
                          : '/suggest_event'
                    }
                    color="#EA580C"
                    radius="sm"
                    size="md"
                  >
                    {activeTab === 'artists' ? 'Add Artist' : activeTab === 'organizers' ? 'Learn More' : 'Add Event'}
                  </Button>
                </Stack>
              </Paper>
            )}
          </Tabs.Panel>
        </Tabs>

        {/* Filter Drawer */}
        <Drawer
          opened={filterDrawerOpened}
          onClose={() => setFilterDrawerOpened(false)}
          position="right"
          size={isMobile ? '85%' : 'md'}
          styles={{
            root: { zIndex: 1000 },
            header: {
              backgroundColor: '#18181B',
              color: '#f2f2f2',
              borderBottom: '1px solid #333',
            },
            body: {
              backgroundColor: '#18181B',
              color: '#f2f2f2',
              minHeight: '100vh',
            },
            close: {
              color: '#f2f2f2',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            },
          }}
        >
          <Stack p="md">
            {/* Super Admin Edit Button */}
            {area.can_edit && (
              <Group justify="center">
                <Button
                  component={Link}
                  href={`/city/${area.slug}/edit`}
                  size="xl"
                  fullWidth
                  color="orange"
                  styles={{
                    root: {
                      backgroundColor: '#EA580C',
                      padding: '1rem 2rem',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                    },
                  }}
                >
                  Edit Area
                </Button>
              </Group>
            )}
            <Text fw={600}>Filters</Text>
            <Divider />
            <TextInput
              label="Event name"
              placeholder={searchNameInput ? undefined : 'Any event'}
              value={searchNameInput}
              onChange={(e) => handleSearchNameChange(e.currentTarget.value)}
              rightSection={debouncingSearch && <Loader size="xs" color="orange" />}
            />
            <MultiSelect
              label="Dance style"
              data={danceStyleOptions}
              value={selectedDanceStyles}
              onChange={handleDanceStyleChange}
              placeholder={selectedDanceStyles.length ? undefined : 'Any dance style'}
              clearable
              searchable
              rightSection={debouncingSearch && <Loader size="xs" color="orange" />}
            />
            <MultiSelect
              label="Event type"
              data={eventTypeOptions}
              value={selectedEventTypes}
              onChange={handleEventTypeChange}
              placeholder={selectedEventTypes.length ? undefined : 'Any event type'}
              clearable
              searchable
              rightSection={debouncingSearch && <Loader size="xs" color="orange" />}
            />
            <Group justify="apart" mt="xl">
              <Button
                variant="light"
                color="orange"
                size="md"
                fullWidth
                className="clear-button"
                styles={{
                  root: {
                    borderColor: '#EA580C10',
                    '&:disabled': {
                      borderColor: '#3F3F46',
                      color: '#71717A',
                    },
                  },
                  label: { fontSize: 12 },
                }}
                onClick={clearFilters}
                disabled={!activeFilterCount || debouncingSearch}
              >
                Clear all
              </Button>
              <Button
                variant="filled"
                fullWidth
                color="#EA580C"
                size="md"
                onClick={() => {
                  setFilterDrawerOpened(false);
                  applyFilters();
                }}
                styles={() => ({ label: { fontSize: 12 } })}
                disabled={debouncingSearch}
              >
                Apply Filters
                {isLoading && <Loader color="white" size="xs" ml="xs" />}
              </Button>
            </Group>
          </Stack>
        </Drawer>

        {/* Dynamic Drawer for Events and Artists */}
        <DynamicDrawer
          opened={drawerOpened}
          type={drawerContentType}
          data={
            drawerContentType === 'event'
              ? selectedEvent
              : drawerContentType === 'artist'
                ? selectedArtist
                : selectedOrganizer
          }
          onClose={() => setDrawerOpened(false)}
        />
      </Container>
    </MantineProvider>
  );
}
